import {
    RiCheckboxCircleFill,
    RiCloseCircleFill,
    RiCloseLine,
    RiErrorWarningFill,
    RiInformationFill,
} from '@remixicon/react';
import { cloneElement, ComponentProps, forwardRef } from 'react';
import { Flip, toast, ToastOptions } from 'react-toastify/unstyled';
import { cn } from '~/helpers/tw';
import Button from './buttons';
import Icon from './icon';

const iconMap = {
    info: <RiInformationFill className="text-primary-darker" />,
    error: <RiCloseCircleFill className="text-error-darker" />,
    success: <RiCheckboxCircleFill className="text-success-darker" />,
    warning: <RiErrorWarningFill className="text-warning-darker" />,
    default: <RiInformationFill className="text-primary-darker" />, // same as info
};

interface ToastData {
    title: string;
    message?: string;
    close?: () => void;
}

type ToastProps = Pick<ToastOptions<ToastData>, 'type'> &
    ToastData &
    ComponentProps<'div'>;

export const Toast = forwardRef<HTMLDivElement, ToastProps>(
    ({ type, title, message, close, className }, ref) => (
        <div
            ref={ref}
            className={cn(
                'flex h-full w-full max-w-[28.625rem] items-start gap-3 rounded-md border border-blue-500 bg-blue-50 p-4 sm:min-w-96 dark:bg-blue-200',
                {
                    'border border-red-500 bg-red-50 dark:border-red-300 dark:bg-red-100':
                        type === 'error',
                    'border border-yellow-500 bg-yellow-50 dark:border-yellow-500 dark:bg-yellow-200':
                        type === 'warning',
                    'border border-green-500 bg-green-50 dark:border-green-500 dark:bg-green-200':
                        type === 'success',
                },
                className
            )}
        >
            <div>{cloneElement(iconMap[type ?? 'default'], { size: 24 })}</div>
            <div className="flex-1">
                <p className="title-m text-gray-950">{title}</p>
                {message && (
                    <p className="text-gray-700 dark:text-gray-800">
                        {message}
                    </p>
                )}
            </div>
            {close && (
                <Button
                    variant="ghost"
                    className="ml-auto"
                    onClick={() => close()}
                    icon={{
                        position: 'start',
                        component: (
                            <Icon
                                size={24}
                                component={RiCloseLine}
                                className="text-gray-950"
                            />
                        ),
                    }}
                />
            )}
        </div>
    )
);

Toast.displayName = 'Toast';

export const sendNotification = ({
    data,
    type,
    className,
    ...options
}: ToastOptions<ToastData>) => {
    // If there's no data or title, don't show an (empty) notification.
    if (!data || !data.title) return null;

    return toast(
        ({ closeToast }) => (
            <Toast
                title={data.title}
                message={data?.message}
                type={type}
                close={closeToast}
            />
        ),
        {
            position: 'top-center',
            autoClose: 4000,
            hideProgressBar: true,
            transition: Flip,
            closeOnClick: true,
            closeButton: false,
            // Ignore default Toast styles so we can use a custom component
            className: cn('!w-auto !rounded-md !p-0', className),
            ...options,
        }
    );
};
